import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Mail, MapPin, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Welcome — we'll be in touch when the gate opens.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-ink text-cream relative" data-testid="site-footer">
      {/* Iron Talon skyline — the exact line from the brand logo */}
      <div className="absolute top-0 left-0 right-0 h-24 lg:h-20 overflow-hidden pointer-events-none" aria-hidden="true">
        <img
          src="/brand/skyline-logo-cream.svg"
          alt=""
          className="absolute bottom-0 left-0 w-full h-full"
          style={{ opacity: 0.28, objectFit: "cover", objectPosition: "bottom" }}
        />
      </div>
      <div className="container-x py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 pt-28 lg:pt-36">
        {/* Brand */}
        <div className="lg:col-span-5">          <img
            src="/brand/iron-talon-logo-circle.png?v=3"
            alt="Iron Talon Farm"
            className="h-36 w-auto -ml-3 mb-4"
          />
          <p className="max-w-md text-cream/70 leading-relaxed text-[15px]">
            A 50-acre historic farm and retreat in Kempton, Pennsylvania.
            Rooted in hospitality, service, and the quiet idea that iron sharpens iron.
          </p>

          <form
            onSubmit={onSubscribe}
            className="mt-10 max-w-md"
            data-testid="newsletter-form"
          >
            <label className="block text-[11px] uppercase tracking-[0.22em] text-cream/50 mb-3">
              The Quiet Letter — occasional notes from the farm
            </label>
            <div className="flex items-end gap-4 border-b border-cream/30 focus-within:border-cream transition-colors">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent border-0 outline-none py-3 text-cream placeholder:text-cream/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="text-[11px] uppercase tracking-[0.22em] py-3 text-cream/80 hover:text-cream transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Subscribe"}
              </button>
            </div>
          </form>
        </div>

        {/* Explore */}
        <div className="lg:col-span-2 lg:col-start-6">
          <div className="overline text-cream/40 mb-6">Explore</div>
          <ul className="space-y-3 text-cream/80">
            <li><Link to="/farm" className="link-underline hover:text-cream">About the Farm</Link></li>
            <li><Link to="/shadow-foundation" className="link-underline hover:text-cream">The Shadow Foundation</Link></li>
            <li><Link to="/new-leaf" className="link-underline hover:text-cream">New Leaf Wellness</Link></li>
            <li><Link to="/farm#gallery" className="link-underline hover:text-cream">Gallery</Link></li>
          </ul>
        </div>

        {/* Plan a Visit */}
        <div className="lg:col-span-2">
          <div className="overline text-cream/40 mb-6">Plan a Visit</div>
          <ul className="space-y-3 text-cream/80">
            <li><Link to="/stay" className="link-underline hover:text-cream">Cottages</Link></li>
            <li><Link to="/amenities" className="link-underline hover:text-cream">Beyond the Cottage · Amenities</Link></li>
            <li><Link to="/local-guide" className="link-underline hover:text-cream">Local Guide</Link></li>
            <li><Link to="/visit" className="link-underline hover:text-cream">Contact</Link></li>
            <li><Link to="/donate" className="link-underline hover:text-cream">Donate</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div className="lg:col-span-3 lg:col-start-10">
          <div className="overline text-cream/40 mb-6">Find Us</div>
          <ul className="space-y-4 text-cream/80 text-[15px]">
            <li className="flex gap-3 items-start">
              <MapPin size={16} strokeWidth={1.4} className="mt-1 text-cream/60" />
              <span>Kempton, Pennsylvania<br />Near Hawk Mountain &<br />the Appalachian Trail</span>
            </li>
            <li className="flex gap-3 items-center">
              <Phone size={16} strokeWidth={1.4} className="text-cream/60" />
              <a href="tel:6107097341" className="link-underline hover:text-cream">610-709-7341</a>
            </li>
            <li className="flex gap-3 items-center">
              <Mail size={16} strokeWidth={1.4} className="text-cream/60" />
              <a href="mailto:irontalonfarm@gmail.com" className="link-underline hover:text-cream">irontalonfarm@gmail.com</a>
            </li>
            <li className="flex gap-3 items-center">
              <Instagram size={16} strokeWidth={1.4} className="text-cream/60" />
              <a href="#" className="link-underline hover:text-cream">@irontalonfarm</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="container-x py-6 flex flex-col md:flex-row gap-3 justify-between items-center text-[12px] text-cream/40 tracking-wide">
          <p>© {new Date().getFullYear()} Iron Talon Farm, LLC &nbsp;·&nbsp; The Shadow Foundation 501(c)(3) &nbsp;·&nbsp; New Leaf Wellness Collective LLC</p>
          <p>Tended with care in Kempton, PA.</p>
        </div>
        <div className="container-x pb-6 text-[11px] text-cream/35 leading-relaxed text-center md:text-left">
          The Shadow Foundation is a registered 501(c)(3) nonprofit organization. Donations are tax-deductible to the fullest extent allowed by law.
        </div>
      </div>
    </footer>
  );
}
